import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight, Command, Clock, RotateCcw } from 'lucide-react';
import api from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;
const RECENT_SEARCHES_KEY = 'cp_recent_searches';
const RECENT_RECORDS_KEY = 'cp_recent_records';
const MAX_RECENT = 5;

const TYPE_LABELS = {
  page: 'Pages',
  customer: 'Customers',
  job: 'Jobs',
  estimate: 'Estimates',
  invoice: 'Invoices',
  item: 'Inventory',
  vehicle: 'Vehicles',
  warehouse: 'Warehouses',
  price_book: 'Price Book',
};

const TYPE_ORDER = ['page', 'customer', 'job', 'estimate', 'invoice', 'item', 'vehicle', 'warehouse', 'price_book'];

// --- localStorage helpers ---
function getRecentSearches() {
  try { return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY)) || []; }
  catch { return []; }
}
function saveRecentSearch(query) {
  const q = query.trim();
  if (!q || q.length < 2) return;
  const list = getRecentSearches().filter(s => s !== q);
  list.unshift(q);
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
}

function getRecentRecords() {
  try { return JSON.parse(localStorage.getItem(RECENT_RECORDS_KEY)) || []; }
  catch { return []; }
}
function saveRecentRecord(result) {
  if (result.type === 'page') return; // Don't track pages
  const key = `${result.type}:${result.id || result.url}`;
  const list = getRecentRecords().filter(r => `${r.type}:${r.id || r.url}` !== key);
  list.unshift({ type: result.type, id: result.id, title: result.title, subtitle: result.subtitle, url: result.url, icon: result.icon });
  localStorage.setItem(RECENT_RECORDS_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
}

export default function CommandPalette({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState([]);
  const [recentRecords, setRecentRecords] = useState([]);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  // Focus input + load recents when opened
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults([]);
      setActiveIndex(0);
      setRecentSearches(getRecentSearches());
      setRecentRecords(getRecentRecords());
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Debounced search
  const doSearch = useCallback(async (q) => {
    if (!q.trim()) {
      setResults([]);
      setActiveIndex(0);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`${API}/api/command-palette/search`, { params: { q: q.trim() } });
      setResults(res.data.results || []);
      setActiveIndex(0);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(query), 200);
    return () => clearTimeout(debounceRef.current);
  }, [query, doSearch]);

  // Group results by type
  const grouped = TYPE_ORDER.reduce((acc, type) => {
    const items = results.filter(r => r.type === type);
    if (items.length) acc.push({ type, label: TYPE_LABELS[type] || type, items });
    return acc;
  }, []);

  // Flat list for keyboard nav — includes recents when query is empty
  const isEmptyQuery = !query.trim();
  const emptyItems = isEmptyQuery
    ? [
        ...recentSearches.map(s => ({ _kind: 'search', title: s })),
        ...recentRecords.map(r => ({ _kind: 'record', ...r })),
      ]
    : [];
  const flatResults = isEmptyQuery ? emptyItems : grouped.flatMap(g => g.items);

  // Navigate to selected result
  const selectResult = useCallback((result) => {
    if (result._kind === 'search') {
      // Re-run that search
      setQuery(result.title);
      return;
    }
    // Save to recents
    if (query.trim()) saveRecentSearch(query.trim());
    if (!result._kind) saveRecentRecord(result);
    onClose();
    navigate(result.url);
  }, [navigate, onClose, query]);

  // Keyboard handler
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && flatResults[activeIndex]) {
      e.preventDefault();
      selectResult(flatResults[activeIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }, [flatResults, activeIndex, selectResult, onClose]);

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector(`[data-index="${activeIndex}"]`);
    if (active) active.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!isOpen) return null;

  let flatIndex = -1;
  const hasRecents = isEmptyQuery && (recentSearches.length > 0 || recentRecords.length > 0);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        data-testid="command-palette-backdrop"
      />

      {/* Palette */}
      <div
        className="fixed inset-x-0 top-[12%] z-[101] mx-auto w-full max-w-xl"
        data-testid="command-palette"
      >
        <div className="mx-4 overflow-hidden rounded-xl border border-border bg-background shadow-2xl">
          {/* Search input */}
          <div className="flex items-center gap-3 border-b border-border px-4 py-3">
            <Search className="h-5 w-5 shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search pages, customers, jobs, invoices..."
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              data-testid="command-palette-input"
            />
            {loading && (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            )}
            <button
              onClick={onClose}
              className="flex items-center rounded border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-muted"
              data-testid="command-palette-close"
            >
              ESC
            </button>
          </div>

          {/* Results */}
          <div
            ref={listRef}
            className="max-h-[60vh] overflow-y-auto p-2"
            data-testid="command-palette-results"
          >
            {/* Empty state with recents */}
            {isEmptyQuery && hasRecents && (
              <>
                {recentSearches.length > 0 && (
                  <div className="mb-1" data-testid="recent-searches-section">
                    <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <RotateCcw className="h-3 w-3" />
                      Recent Searches
                    </div>
                    {recentSearches.map((s, i) => {
                      flatIndex++;
                      const idx = flatIndex;
                      const isActive = idx === activeIndex;
                      return (
                        <button
                          key={`search-${i}`}
                          data-index={idx}
                          data-testid={`recent-search-${i}`}
                          onClick={() => setQuery(s)}
                          onMouseEnter={() => setActiveIndex(idx)}
                          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                            isActive ? 'bg-primary/10 text-foreground' : 'text-foreground hover:bg-muted'
                          }`}
                        >
                          <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <span className="truncate">{s}</span>
                          {isActive && <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground ml-auto" />}
                        </button>
                      );
                    })}
                  </div>
                )}
                {recentRecords.length > 0 && (
                  <div className="mb-1" data-testid="recent-records-section">
                    <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Recently Viewed
                    </div>
                    {recentRecords.map((r, i) => {
                      flatIndex++;
                      const idx = flatIndex;
                      const isActive = idx === activeIndex;
                      return (
                        <button
                          key={`record-${i}`}
                          data-index={idx}
                          data-testid={`recent-record-${i}`}
                          onClick={() => selectResult(r)}
                          onMouseEnter={() => setActiveIndex(idx)}
                          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                            isActive ? 'bg-primary/10 text-foreground' : 'text-foreground hover:bg-muted'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="truncate font-medium">{r.title}</div>
                            {r.subtitle && <div className="truncate text-xs text-muted-foreground">{r.subtitle}</div>}
                          </div>
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">
                            {TYPE_LABELS[r.type] || r.type}
                          </span>
                          {isActive && <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* Empty state — no recents */}
            {isEmptyQuery && !hasRecents && (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                Start typing to search across your entire workspace
              </div>
            )}

            {/* No results for query */}
            {query.trim() && !loading && results.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                No results for "<span className="font-medium text-foreground">{query}</span>"
              </div>
            )}

            {/* Search results */}
            {grouped.map(group => (
              <div key={group.type} className="mb-1">
                <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </div>
                {group.items.map(item => {
                  flatIndex++;
                  const idx = flatIndex;
                  const isActive = idx === activeIndex;
                  return (
                    <button
                      key={`${item.type}-${item.id || item.url}`}
                      data-index={idx}
                      data-testid={`command-result-${item.type}-${idx}`}
                      onClick={() => selectResult(item)}
                      onMouseEnter={() => setActiveIndex(idx)}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                        isActive ? 'bg-primary/10 text-foreground' : 'text-foreground hover:bg-muted'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="truncate font-medium">{item.title}</div>
                        {item.subtitle && (
                          <div className="truncate text-xs text-muted-foreground">{item.subtitle}</div>
                        )}
                      </div>
                      {isActive && <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-[10px] text-muted-foreground">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-border bg-muted px-1 py-0.5 font-mono">↑↓</kbd>
                navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-border bg-muted px-1 py-0.5 font-mono">↵</kbd>
                open
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-border bg-muted px-1 py-0.5 font-mono">esc</kbd>
                close
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Command className="h-3 w-3" />
              <span>K</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
